import * as Sentry from '@sentry/react';
import { type Query, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import {
  type GetImageV1PortfoliosPortfolioIdAssetsImagesImageIdGetData,
  type GetImageV1PortfoliosPortfolioIdAssetsImagesImageIdGetError,
  type GetImageV1PortfoliosPortfolioIdAssetsImagesImageIdGetResponse,
  getImageV1PortfoliosPortfolioIdAssetsImagesImageIdGet,
} from '../../generated';

export type GetImageQueryVariables = GetImageV1PortfoliosPortfolioIdAssetsImagesImageIdGetData['path'] & {
  organization_id: string;
};

export type GetImageQuery = GetImageV1PortfoliosPortfolioIdAssetsImagesImageIdGetResponse;

export const useGetImageQuery = <
  TData = GetImageQuery,
  TError = GetImageV1PortfoliosPortfolioIdAssetsImagesImageIdGetError,
>(
  { organization_id, portfolio_id, image_id }: GetImageQueryVariables,
  options?: Omit<UseQueryOptions<GetImageQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetImageQuery, TError, TData>({
    queryKey: ['GetImage', { organization_id, portfolio_id, image_id }],
    queryFn: async () => {
      const { data } = await getImageV1PortfoliosPortfolioIdAssetsImagesImageIdGet({
        path: { portfolio_id, image_id },
        throwOnError: true,
      });
      return data;
    },
    refetchInterval: (query: Query<GetImageQuery, TError>) => {
      const maxRetries = typeof options?.retry === 'number' ? options.retry : 10;
      const maxRetriesReached = query.state.dataUpdateCount >= maxRetries;

      // Poll while the relevant score is pending, or — for hero images only — while mobile
      // readiness is mid-pipeline. Mobile fields resolve all-together via an async SQS→lambda
      // pipeline, so checking one is enough; strict `=== null` skips polling when the backend
      // doesn't return the field at all (e.g. pre-deploy).
      const data = query.state.data;
      const isHero = data?.image_type === 'hero';
      const relevantScore = isHero ? data?.hero_score : data?.vizit_score;
      const shouldPollForScore = relevantScore === null || (isHero && data?.mobile_brand_clarity === null);

      if (shouldPollForScore && !maxRetriesReached) {
        Sentry.addBreadcrumb({
          message: 'Polling for image score',
          level: 'info',
          data: {
            retry_count: query.state.dataUpdateCount,
            max_count: maxRetries,
            asset_id: image_id,
            organization_id,
            portfolio_id,
          },
        });

        return 1000 * (query.state.dataUpdateCount + 1);
      }

      if (shouldPollForScore && maxRetriesReached) {
        Sentry.withScope((scope) => {
          scope.setFingerprint(['GET', '/v1/portfolios/{portfolio_id}/assets/images/{image_id}', 'polling_timeout']);

          Sentry.captureException(new Error('Max retries reached for get image query.'), {
            level: 'warning',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Asset,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetPolling,
            },
            extra: {
              organization_id,
              portfolio_id,
              asset_id: image_id,
              retry_count: query.state.dataUpdateCount,
              max_count: maxRetries,
            },
          });
        });
      }

      return false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });
};

useGetImageQuery.getKey = ({ organization_id, portfolio_id, image_id }: GetImageQueryVariables) => [
  'GetImage',
  { organization_id, portfolio_id, image_id },
];

useGetImageQuery.fetcher = ({ portfolio_id, image_id }: GetImageQueryVariables, _headers?: RequestInit['headers']) => {
  return async () => {
    const { data } = await getImageV1PortfoliosPortfolioIdAssetsImagesImageIdGet({
      path: { portfolio_id, image_id },
      throwOnError: true,
    });
    return data;
  };
};

useGetImageQuery.schema = {
  method: 'get',
  path: '/v1/portfolios/{portfolio_id}/assets/images/{image_id}',
};
